<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import dayjs from 'dayjs';
import InteractiveCalendar from './InteractiveCalendar.vue';
import QuickActionPanel from './QuickActionPanel.vue';
import { useAvailabilityModal } from '@/compostables/useAvailabilityModal';
import { stores } from '@/util/stores.js';

const { isCalendarOpen, calendarOptions, closeCalendar } = useAvailabilityModal();

interface CalendarHandle {
	previousMonthName?: string;
	copyPreviousMonth?: () => void;
	selectAllSaturdays?: () => void;
	selectAllSundays?: () => void;
	selectAllWeekends?: () => void;
}

interface QuickPanelHandle {
	openCustomForm?: () => void;
	closeCustomForm?: () => void;
}

// State
const selectedDates = ref<string[]>([]);
const showQuickPanel = ref(false);
const mobileMode = ref(window.innerWidth < 768);
const effectiveInitialDate = ref<Date | string | null>(null);
const showAddMenu = ref(false);
const calendarRef = ref<CalendarHandle | null>(null);
const addMenuWrapper = ref<HTMLElement | null>(null);
const quickPanelRef = ref<QuickPanelHandle | null>(null);
const modalContainerRef = ref<HTMLElement | null>(null);

// Computed classes for responsive modal container
const containerClasses = computed(() => {
	const base = ['relative flex flex-col overflow-hidden', 'bg-[var(--egs-content-background)] rounded-2xl shadow-modal', 'max-h-[calc(100vh-40px)]'];

	if (mobileMode.value) {
		return [...base, 'max-w-full h-full !rounded-none'].join(' ');
	}

	if (showQuickPanel.value) {
		// Expanded width: calendar + side panel
		return [...base, 'w-[calc(700px+400px+48px+20px)] xl:w-[calc(700px+400px+48px+20px)] lg:w-[calc(640px+380px+48px+20px)] md:w-[calc(580px+340px+48px+20px)] max-w-full'].join(' ');
	}

	// Default width: just calendar
	return [...base, 'w-[calc(700px+48px)] xl:w-[calc(700px+48px)] lg:w-[calc(640px+48px)] md:w-[calc(580px+48px)] max-w-full'].join(' ');
});

const bodyClasses = computed(() => {
	const base = 'flex-1 grid overflow-hidden relative px-6';
	if (!mobileMode.value && showQuickPanel.value) {
		return `${base} grid-cols-[700px_minmax(0,400px)] xl:grid-cols-[700px_minmax(0,400px)] lg:grid-cols-[640px_minmax(0,380px)] md:grid-cols-[580px_minmax(0,340px)] gap-5`;
	}
	return `${base} grid-cols-1`;
});

// Methods
function handleClose() {
	closeCalendar();
	selectedDates.value = [];
	showQuickPanel.value = false;
}

function handleDateClick({ date }: { date: string }) {
	selectedDates.value = [date];
	showQuickPanel.value = true;
}

// Handler for CTA button click from hover card (opens side panel for that date)
function handleSetAvailabilityFromHover({ date }: { date: string }) {
	selectedDates.value = [date];
	showQuickPanel.value = true;
}

function handleDateRangeSelect({ dates }: { dates: string[] }) {
	selectedDates.value = dates;
	showQuickPanel.value = true;
}

async function handleMonthChange(newDate: Date) {
	effectiveInitialDate.value = newDate;
	await loadVisibleMonth(newDate);
}

function getSmartDefaultDate(date: Date | string = new Date()) {
	const now = dayjs(date);
	const endOfMonth = now.endOf('month');
	const daysRemaining = endOfMonth.diff(now, 'day');

	if (daysRemaining <= 7) {
		return now.add(1, 'month').startOf('month').toDate();
	}

	return now.toDate();
}

async function handleCopyPreviousMonth({ sourceMonth, targetMonth }: { sourceMonth: string; targetMonth: string }) {
	// Copy availability from source month to target month
	try {
		const result = await stores.availabilityStore.copyMonthAvailability(sourceMonth, targetMonth);
		if (result.success) {
			// Refresh the calendar data
			const start = dayjs(targetMonth + '-01')
				.startOf('month')
				.subtract(7, 'day');
			const end = dayjs(targetMonth + '-01')
				.endOf('month')
				.add(7, 'day');
			await stores.availabilityStore.fetchMyBlocks(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
		}
	} catch (error) {
		// Error handled silently - toast notification should be shown by store
	}
}

function handleQuickPanelClose() {
	showQuickPanel.value = false;
	selectedDates.value = [];
}

async function handleQuickPanelSaved() {
	const baseDate = effectiveInitialDate.value ? dayjs(effectiveInitialDate.value) : dayjs();
	const start = baseDate.startOf('month').subtract(7, 'day');
	const end = baseDate.endOf('month').add(7, 'day');

	await stores.availabilityStore.fetchMyBlocks(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));

	await stores.availabilityStore.fetchMySuggestions(baseDate.startOf('month').format('YYYY-MM-DD'), 90);
}

async function openAddForm() {
	// Reuse the QuickActionPanel shell + embedded custom form
	const base = effectiveInitialDate.value ? dayjs(effectiveInitialDate.value) : dayjs();
	selectedDates.value = [base.format('YYYY-MM-DD')];
	showQuickPanel.value = true;
	showAddMenu.value = false;
	await nextTick();
	quickPanelRef.value?.openCustomForm?.();
}

function toggleAddMenu() {
	showAddMenu.value = !showAddMenu.value;
}

// Copy previous month (accessed from calendar ref)
const previousMonthName = computed(() => {
	return calendarRef.value?.previousMonthName || dayjs(effectiveInitialDate.value).subtract(1, 'month').format('MMMM');
});

function copyPreviousMonth() {
	showAddMenu.value = false;
	calendarRef.value?.copyPreviousMonth?.();
}

// Quick selection actions (delegate to calendar)
function selectAllSaturdays() {
	calendarRef.value?.selectAllSaturdays?.();
	showAddMenu.value = false;
}

function selectAllSundays() {
	calendarRef.value?.selectAllSundays?.();
	showAddMenu.value = false;
}

function selectAllWeekends() {
	calendarRef.value?.selectAllWeekends?.();
	showAddMenu.value = false;
}

function handleResize() {
	mobileMode.value = window.innerWidth < 768;
}

function handleKeyDown(e: KeyboardEvent) {
	if (e.key === 'Escape' && isCalendarOpen.value) {
		if (showAddMenu.value) {
			showAddMenu.value = false;
		} else if (showQuickPanel.value) {
			handleQuickPanelClose();
		} else {
			handleClose();
		}
	}
}

function handleClickOutside(e: MouseEvent) {
	const target = e.target instanceof Node ? e.target : null;
	if (showAddMenu.value && addMenuWrapper.value && target && !addMenuWrapper.value.contains(target)) {
		showAddMenu.value = false;
	}
}

// Lifecycle
onMounted(() => {
	window.addEventListener('resize', handleResize);
	document.addEventListener('keydown', handleKeyDown);
	document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize);
	document.removeEventListener('keydown', handleKeyDown);
	document.removeEventListener('click', handleClickOutside);
	if (isCalendarOpen.value) {
		unlockBodyScroll();
	}
});

// Body scroll lock helpers
function lockBodyScroll() {
	const scrollY = window.scrollY;
	document.documentElement.style.overflow = 'hidden';
	document.body.style.position = 'fixed';
	document.body.style.top = `-${scrollY}px`;
	document.body.style.left = '0';
	document.body.style.right = '0';
	document.body.style.overflow = 'hidden';
	document.body.dataset['scrollY'] = String(scrollY);
}

function unlockBodyScroll() {
	const scrollY = document.body.dataset['scrollY'] || '0';
	document.documentElement.style.overflow = '';
	document.body.style.position = '';
	document.body.style.top = '';
	document.body.style.left = '';
	document.body.style.right = '';
	document.body.style.overflow = '';
	window.scrollTo(0, parseInt(scrollY));
	delete document.body.dataset['scrollY'];
}

async function loadVisibleMonth(date: Date | string) {
	const monthStart = dayjs(date).startOf('month');
	const start = monthStart.subtract(7, 'day');
	const end = monthStart.endOf('month').add(7, 'day');

	await stores.availabilityStore.fetchMyBlocks(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
	await stores.availabilityStore.fetchMySuggestions(monthStart.format('YYYY-MM-DD'), 90);
}

// Watch for modal open
watch(
	isCalendarOpen,
	async (open) => {
		if (open) {
			lockBodyScroll();

			const requestedInitialDate = calendarOptions.value?.initialDate || new Date();
			const requestedDay = dayjs(requestedInitialDate);
			effectiveInitialDate.value = requestedDay.isSame(dayjs(), 'day') ? getSmartDefaultDate(requestedInitialDate) : requestedInitialDate;

			await stores.availabilityStore.initialize();
			stores.availabilityStore.suggestions = [];
			await loadVisibleMonth(effectiveInitialDate.value);

			if (calendarOptions.value.selectedDates?.length > 0) {
				selectedDates.value = [...calendarOptions.value.selectedDates];
			}
		} else {
			unlockBodyScroll();
			selectedDates.value = [];
			showQuickPanel.value = false;
			effectiveInitialDate.value = null;
		}
	},
	{ immediate: true }
);
</script>

<template>
	<Teleport to="body">
		<Transition name="modal-fade">
			<div v-if="isCalendarOpen" class="fixed inset-0 bg-black/50 backdrop-blur-sm grid place-items-center z-[9998] overflow-hidden p-5 box-border">
				<div ref="modalContainerRef" :class="containerClasses">
					<!-- Modal Header -->
					<div class="flex items-center justify-between py-4 px-6 border-b border-surface bg-[var(--egs-content-background)]">
						<div class="flex items-center gap-2.5">
							<font-awesome-icon :icon="['fas', 'calendar-alt']" class="text-primary text-xl" />
							<h2 class="text-lg font-semibold text-color m-0">My Availability</h2>
						</div>
						<div class="flex items-center gap-2">
							<!-- Add button with dropdown menu -->
							<div ref="addMenuWrapper" class="relative">
								<button class="flex items-center gap-1.5 py-2 px-3.5 border-none rounded-lg bg-gray-600 text-white text-sm font-medium cursor-pointer transition-all hover:bg-gray-700" @click="toggleAddMenu">
									<font-awesome-icon :icon="['fas', 'plus']" />
									<span v-if="!mobileMode">Quick Add</span>
									<font-awesome-icon :icon="['fas', 'chevron-down']" class="text-[0.65rem] ml-1 transition-transform duration-150" :class="{ 'rotate-180': showAddMenu }" />
								</button>
								<Transition name="menu-fade">
									<div v-if="showAddMenu" class="absolute top-[calc(100%+6px)] right-0 z-[100] min-w-[200px] bg-[var(--egs-content-background)] border border-surface rounded-xl shadow-dropdown p-1.5">
										<button
											class="flex items-center gap-2.5 w-full py-2.5 px-3 border-none rounded-md bg-transparent text-color text-sm font-medium cursor-pointer transition-colors text-left hover:bg-[var(--egs-content-hover)]"
											@click="openAddForm"
										>
											<font-awesome-icon :icon="['fas', 'plus']" />
											<span>Add availability...</span>
										</button>
										<div class="h-px bg-surface my-1"></div>
										<div class="py-1.5 px-3 text-[0.7rem] font-semibold text-color-secondary uppercase tracking-wide">Quick select</div>
										<button
											class="flex items-center gap-2.5 w-full py-2.5 px-3 border-none rounded-md bg-transparent text-color text-sm font-medium cursor-pointer transition-colors text-left hover:bg-[var(--egs-content-hover)]"
											@click="selectAllSaturdays"
										>
											<font-awesome-icon :icon="['fas', 'calendar-day']" />
											<span>All Saturdays</span>
										</button>
										<button
											class="flex items-center gap-2.5 w-full py-2.5 px-3 border-none rounded-md bg-transparent text-color text-sm font-medium cursor-pointer transition-colors text-left hover:bg-[var(--egs-content-hover)]"
											@click="selectAllSundays"
										>
											<font-awesome-icon :icon="['fas', 'calendar-day']" />
											<span>All Sundays</span>
										</button>
										<button
											class="flex items-center gap-2.5 w-full py-2.5 px-3 border-none rounded-md bg-transparent text-primary text-sm font-medium cursor-pointer transition-colors text-left hover:bg-primary/[0.08]"
											@click="selectAllWeekends"
										>
											<font-awesome-icon :icon="['fas', 'calendar-week']" />
											<span>All Weekends</span>
										</button>
										<div class="h-px bg-surface my-1"></div>
										<button
											class="flex items-center gap-2.5 w-full py-2.5 px-3 border-none rounded-md bg-transparent text-color text-sm font-medium cursor-pointer transition-colors text-left hover:bg-[var(--egs-content-hover)]"
											@click="copyPreviousMonth"
										>
											<font-awesome-icon :icon="['fas', 'copy']" />
											<span>Copy {{ previousMonthName }}</span>
										</button>
									</div>
								</Transition>
							</div>
							<button
								class="flex items-center justify-center w-9 h-9 border-none rounded-lg bg-[var(--egs-content-hover)] text-color-secondary cursor-pointer transition-all hover:bg-[var(--egs-content-border)] hover:text-color"
								aria-label="Close"
								@click="handleClose"
							>
								<font-awesome-icon :icon="['fas', 'times']" />
							</button>
						</div>
					</div>

					<!-- Modal Body -->
					<div :class="bodyClasses">
						<!-- Calendar View -->
						<div class="w-[700px] xl:w-[700px] lg:w-[640px] md:w-[580px] h-[540px] min-w-0 overflow-hidden flex flex-col">
							<InteractiveCalendar
								ref="calendarRef"
								:initialDate="effectiveInitialDate || new Date()"
								:selectedDates="selectedDates"
								:mobileMode="mobileMode"
								@dateClick="handleDateClick"
								@dateRangeSelect="handleDateRangeSelect"
								@monthChange="handleMonthChange"
								@setAvailabilityFromHover="handleSetAvailabilityFromHover"
								@copyPreviousMonth="handleCopyPreviousMonth"
							/>
						</div>

						<!-- Desktop Side Panel (expands modal to the right) -->
						<Transition name="side-reveal">
							<div v-if="!mobileMode && showQuickPanel" class="min-w-0 w-full border-l border-surface bg-[var(--egs-content-background)] flex flex-col overflow-hidden pr-3 box-border">
								<div class="h-full flex flex-col min-w-0 min-h-0">
									<QuickActionPanel ref="quickPanelRef" :visible="showQuickPanel" :selectedDates="selectedDates" :mobileMode="false" @close="handleQuickPanelClose" @saved="handleQuickPanelSaved" />
								</div>
							</div>
						</Transition>

						<!-- Mobile Quick Action Panel (bottom sheet) -->
						<QuickActionPanel v-if="mobileMode" ref="quickPanelRef" :visible="showQuickPanel" :selectedDates="selectedDates" :mobileMode="mobileMode" @close="handleQuickPanelClose" @saved="handleQuickPanelSaved" />
					</div>

					<!-- Loading Overlay -->
					<Transition name="fade">
						<div v-if="stores.availabilityStore.loading" class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/80 text-color dark:bg-black/80">
							<div class="w-8 h-8 border-[3px] border-surface border-t-primary rounded-full animate-spin"></div>
							<span>Loading...</span>
						</div>
					</Transition>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>
